// dashboard/src/components/Interact/MicTestModal.tsx
//
// A recovery-time microphone test. Opened from any capture-failure surface
// (the record screen's silence gate, or the analysis screen's "couldn't detect
// anyone speaking"). It does NOT assume the mic is broken — a failed take is
// often just someone freezing — so it's one branch of a fork the caller offers:
// "Try again" vs. "Test my microphone". The email-IT boilerplate is revealed
// only if the test itself fails, so we never send healthy users to IT.
//
// Depends only on helpers/audioDiagnostics (already in the codebase), so it does
// not require MediaCheck to be integrated.

import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import RecordRTC from 'recordrtc';
import {
  analyzeBlobSignal,
  classifyMediaError,
  createSignalMonitor,
  getTrackDiagnostics,
  watchAudioTrackMute,
  verdictFromPeak,
  type SignalMonitor,
  type TrackDiagnostics,
} from '../../helpers/audioDiagnostics';

type Phase = 'idle' | 'requesting' | 'ready' | 'recording' | 'analyzing' | 'done';
type Verdict = 'pass' | 'warn' | 'fail';

interface MicTestModalProps {
  isOpen: boolean;
  onClose: () => void;
  supportEmail: string;
  // If provided, a passing test shows a "Record again" button that calls this.
  onRetry?: () => void;
}

const RECORD_MS = 5000;
// Live RMS from the monitor is smaller than a decoded peak; below this the mic
// produced essentially nothing while recording.
const LIVE_FLAT_THRESHOLD = 0.006;

const MicTestModal: React.FC<MicTestModalProps> = ({ isOpen, onClose, supportEmail, onRetry }) => {
  const [phase, setPhase] = useState<Phase>('idle');
  const [liveLevel, setLiveLevel] = useState(0);
  const [verdict, setVerdict] = useState<Verdict | null>(null);
  const [reasons, setReasons] = useState<string[]>([]);
  const [reportText, setReportText] = useState('');
  const [playbackUrl, setPlaybackUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const streamRef = useRef<MediaStream | null>(null);
  // Bumped on every acquisition and every teardown, so a getUserMedia call that
  // resolves after close/unmount is recognised as stale and released at once.
  const acquireSeqRef = useRef(0);
  const monitorRef = useRef<SignalMonitor | null>(null);
  const meterIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const detachMuteRef = useRef<(() => void) | null>(null);
  const muteFiredRef = useRef(false);
  const recorderRef = useRef<any>(null);
  const recordTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const playbackUrlRef = useRef<string | null>(null);

  const teardown = () => {
    acquireSeqRef.current++;
    if (recordTimerRef.current) clearTimeout(recordTimerRef.current);
    recordTimerRef.current = null;
    if (meterIntervalRef.current) clearInterval(meterIntervalRef.current);
    meterIntervalRef.current = null;
    monitorRef.current?.stop();
    monitorRef.current = null;
    detachMuteRef.current?.();
    detachMuteRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    try { recorderRef.current?.destroy(); } catch { /* noop */ }
    recorderRef.current = null;
    if (playbackUrlRef.current) {
      URL.revokeObjectURL(playbackUrlRef.current);
      playbackUrlRef.current = null;
    }
  };

  const resetState = () => {
    setPhase('idle');
    setLiveLevel(0);
    setVerdict(null);
    setReasons([]);
    setReportText('');
    setPlaybackUrl(null);
    setError(null);
    setCopied(false);
    muteFiredRef.current = false;
  };

  const beginStream = async () => {
    const seq = ++acquireSeqRef.current;
    setError(null);
    setPhase('requesting');
    try {
      if (!('mediaDevices' in navigator)) {
        throw new Error('This browser does not expose navigator.mediaDevices.');
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (seq !== acquireSeqRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      streamRef.current = stream;

      muteFiredRef.current = false;
      detachMuteRef.current = watchAudioTrackMute(stream, (event) => {
        if (event === 'mute') muteFiredRef.current = true;
      });

      const monitor = createSignalMonitor(stream);
      monitorRef.current = monitor;
      meterIntervalRef.current = setInterval(() => setLiveLevel(monitor.getCurrentRms()), 100);

      setPhase('ready');
    } catch (e: any) {
      if (seq !== acquireSeqRef.current) return;
      const classified = await classifyMediaError(e);
      if (seq !== acquireSeqRef.current) return;
      console.warn(`[mic-test] getUserMedia failed (${classified.kind}): ${classified.detail}`);
      setError(classified.message);
      setPhase('idle');
    }
  };

  // Acquire on open, release on close. MicTestModal owns its own device access;
  // callers that hold a live stream (the record screen) release it before opening
  // so we don't contend for the same device on exclusive-mode Windows drivers.
  useEffect(() => {
    if (isOpen) {
      resetState();
      beginStream();
    } else {
      teardown();
      resetState();
    }
    return () => {
      teardown();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const runTest = async () => {
    const stream = streamRef.current;
    const monitor = monitorRef.current;
    if (!stream || !monitor) return;
    // teardown() bumps acquireSeqRef, so a test cancelled by closing the modal
    // sees a changed seq after each await and bails out without touching state.
    const seq = acquireSeqRef.current;
    const aborted = () => seq !== acquireSeqRef.current;

    setPhase('recording');
    const recorder = new RecordRTC(stream, { type: 'audio' });
    recorderRef.current = recorder;
    recorder.startRecording();

    // teardown() clears this timer, so on cancel the promise never resolves and
    // the rest of the test simply never runs.
    await new Promise((r) => { recordTimerRef.current = setTimeout(r, RECORD_MS); });
    recordTimerRef.current = null;
    if (aborted()) return;

    setPhase('analyzing');
    const liveLevelMax = monitor.getMaxRms();
    const muteFired = muteFiredRef.current;
    const tracks: TrackDiagnostics[] = getTrackDiagnostics(stream);

    const blob: Blob = await new Promise((resolve) => {
      recorder.stopRecording(() => resolve(recorder.getBlob()));
    });
    if (aborted()) return;

    let durationSec: number | null = null;
    let peak: number | null = null;
    const nextReasons: string[] = [];
    let decodeFailed = false;

    try {
      const a = await analyzeBlobSignal(blob);
      durationSec = a.durationSec;
      peak = a.peak;
    } catch (e: any) {
      decodeFailed = true;
      nextReasons.push(`The test recording could not be decoded (${e?.message || e}).`);
    }
    if (aborted()) return;

    let nextVerdict: Verdict = 'pass';
    if (decodeFailed || blob.size < 1024) {
      nextVerdict = 'fail';
      if (blob.size < 1024) nextReasons.push(`The recording was only ${blob.size} bytes — effectively empty.`);
    } else {
      const v = verdictFromPeak(peak ?? 0);
      if (v === 'silent') {
        nextVerdict = 'fail';
        nextReasons.push('Sound was recorded but it came out silent.');
        if (muteFired || tracks.some((t) => t.kind === 'audio' && t.muted)) {
          nextReasons.push(
            'Your microphone was taken over during the test (it went muted at the system level). ' +
              'That usually means another app or an IT policy is controlling it — close apps like ' +
              'Teams, Zoom, or Webex and check your system microphone settings, then test again.'
          );
        } else if (liveLevelMax > LIVE_FLAT_THRESHOLD * 3) {
          nextReasons.push(
            'Sound reached the browser (the meter moved) but the recording was silent — that points ' +
              'at the recording path rather than your hardware.'
          );
        } else {
          nextReasons.push(
            'No sound reached the browser. Check that the right microphone is selected and unmuted, ' +
              'and that nothing else is using it.'
          );
        }
      } else if (v === 'quiet') {
        nextVerdict = 'warn';
        nextReasons.push('Your microphone works, but it is very quiet. Speak up or move closer.');
      } else {
        nextReasons.push('Your microphone is working — a clear signal was captured.');
      }
    }

    if (playbackUrlRef.current) URL.revokeObjectURL(playbackUrlRef.current);
    const url = URL.createObjectURL(blob);
    playbackUrlRef.current = url;
    setPlaybackUrl(url);

    setReportText(
      JSON.stringify(
        {
          verdict: nextVerdict,
          blobSizeBytes: blob.size,
          durationSec,
          recordedPeak: peak,
          liveLevelMax,
          muteFired,
          tracks,
          userAgent: navigator.userAgent,
        },
        null,
        2
      )
    );
    setReasons(nextReasons);
    setVerdict(nextVerdict);
    setPhase('done');
  };

  const copyReport = async () => {
    if (!reportText) return;
    try {
      await navigator.clipboard.writeText(reportText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const el = document.getElementById('mictest-report') as HTMLTextAreaElement | null;
      el?.select();
    }
  };

  const mailtoHref = () => {
    const subject = encodeURIComponent('Role play microphone problem');
    const body = encodeURIComponent(
      'What I was doing when this happened:\n\n\n' +
        '--- technical details (paste from the app if not shown below) ---\n' +
        reportText
    );
    return `mailto:${supportEmail}?subject=${subject}&body=${body}`;
  };

  const verdictColor = (v: Verdict) => (v === 'pass' ? '#28a745' : v === 'warn' ? '#f0ad4e' : '#dc3545');
  const verdictLabel = (v: Verdict) => (v === 'pass' ? 'Working' : v === 'warn' ? 'Check this' : 'Not working');
  const meterPct = Math.min(100, Math.round(liveLevel * 300));

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>Test your microphone</ModalHeader>
      <ModalBody>
        <p style={{ marginBottom: 12, color: '#495057' }}>
          This records a short test using the same microphone the role play uses, then tells you whether
          your device actually captured sound. Make sure the right mic is selected and unmuted, and close
          any app that might be using it (Teams, Zoom, Webex), then record a few words.
        </p>

        {error && (
          <div style={{ padding: 12, background: '#f8d7da', color: '#721c24', borderRadius: 6, marginBottom: 12 }}>
            {error}
          </div>
        )}

        {(phase === 'requesting') && <p>Requesting microphone access…</p>}

        {(phase === 'ready' || phase === 'recording' || phase === 'analyzing') && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <div style={{ fontSize: 13, color: '#6c757d', marginBottom: 4 }}>Live input level (talk to see it move):</div>
              <div style={{ height: 18, background: '#e9ecef', borderRadius: 9, overflow: 'hidden' }}>
                <div style={{
                  height: '100%', width: `${meterPct}%`,
                  background: meterPct > 6 ? '#28a745' : '#adb5bd', transition: 'width 80ms linear',
                }} />
              </div>
            </div>
            {phase === 'ready' && (
              <Button color="info" style={{ color: 'white' }} onClick={runTest}>
                Click here and speak for {RECORD_MS / 1000} seconds to test your microphone
              </Button>
            )}
            {phase === 'recording' && <p style={{ fontWeight: 'bold' }}>Recording… say a few words.</p>}
            {phase === 'analyzing' && <p>Analyzing…</p>}
          </div>
        )}

        {phase === 'done' && verdict && (
          <div>
            <div style={{
              padding: 12, borderRadius: 6, color: 'white',
              background: verdictColor(verdict), fontWeight: 'bold', marginBottom: 12,
            }}>
              {verdictLabel(verdict)}
            </div>

            <ul style={{ paddingLeft: 18, marginBottom: 12 }}>
              {reasons.map((r, i) => <li key={i} style={{ marginBottom: 4 }}>{r}</li>)}
            </ul>

            {playbackUrl && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 13, color: '#6c757d', marginBottom: 4 }}>Play back what was recorded:</div>
                <audio src={playbackUrl} controls style={{ width: '100%' }} />
              </div>
            )}

            <Button color="secondary" size="sm" onClick={() => { teardown(); resetState(); beginStream(); }}>
              Test again
            </Button>

            {/* Email-IT boilerplate: only after a failed test, never for a healthy mic. */}
            {verdict === 'fail' && (
              <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #dee2e6' }}>
                <p style={{ marginBottom: 8 }}>
                  Still not working? Your device or organization may be blocking microphone access. Send these
                  details to <strong>{supportEmail}</strong> and we'll help.
                </p>
                <textarea
                  id="mictest-report"
                  readOnly
                  value={reportText}
                  onFocus={(e) => e.currentTarget.select()}
                  style={{
                    width: '100%', height: 160, fontFamily: 'monospace', fontSize: 12,
                    padding: 10, borderRadius: 6, border: '1px solid #ced4da', resize: 'vertical',
                  }}
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <Button color="primary" size="sm" onClick={copyReport}>
                    {copied ? 'Copied ✓' : 'Copy details'}
                  </Button>
                  <a href={mailtoHref()} style={{ textDecoration: 'none' }}>
                    <Button color="info" size="sm" style={{ color: 'white' }}>Email support</Button>
                  </a>
                </div>
              </div>
            )}
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        {phase === 'done' && verdict === 'pass' && onRetry && (
          <span style={{ color: '#28a745', marginRight: 'auto' }}>Your mic works — Try again.</span>
        )}
        {phase === 'done' && verdict === 'pass' && onRetry ? (
          <Button color="success" onClick={onRetry}>Return to the conversation</Button>
        ) : (
          <Button color="secondary" onClick={onClose}>{phase === 'recording' ? 'Cancel' : 'Done'}</Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default MicTestModal;